import AddressValidationModal from "@electricMotorShop/Components/AddressValidationModal";
import {
    AddressValidationResult,
    AddressValidationResultModel,
    ValidationAddressModel,
} from "@electricMotorShop/Services/AddressValidationService";
import validateInputAddress from "@electricMotorShop/Store/Components/AddressValidation/Handlers/ValidateInputAddress";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import validateEmail from "@insite/client-framework/Common/Utilities/validateEmail";
import validatePhone from "@insite/client-framework/Common/Utilities/validatePhone";
import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import siteMessage from "@insite/client-framework/SiteMessage";
import updateCart from "@insite/client-framework/Store/Pages/CheckoutShipping/Handlers/UpdateCart";
import translate from "@insite/client-framework/Translate";
import {
    AddressFieldDisplayCollectionModel,
    AddressFieldDisplayModel,
    BaseAddressModel,
    BillToModel,
    CountryModel,
    ShipToModel,
} from "@insite/client-framework/Types/ApiModels";
import CustomerAddressFormFields, {
    CustomerAddressFormFieldStyles,
} from "@insite/content-library/Components/CustomerAddressFormFields";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { TextFieldProps } from "@insite/mobius/TextField";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import cloneDeep from "lodash/cloneDeep";
import React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps<Address extends BillToModel | ShipToModel> {
    address: Address;
    countries: CountryModel[];
    addressFieldDisplayCollection: AddressFieldDisplayCollectionModel;
    saveButtonTextOverride?: string;
    onCancel?: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
    onSubmit: (event: React.FormEvent<HTMLFormElement>, address: Address) => void;
    extendedStyles?: CustomerAddressFormStyles;
    inputAddress: ValidationAddressModel;
    addressValidationResult: AddressValidationResult;
}

type Props = ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const mapStateToProps = () => ({});

const mapDispatchToProps = {
    validateInputAddress,
    updateCart: makeHandlerChainAwaitable(updateCart),
};

interface State<Address extends BillToModel | ShipToModel> {
    address: Address;
    countryLookup?: CountryModel;
    formErrors: FormErrorsState;
    formSubmitAttempted: boolean;
    isSubmitting: boolean;
    isValidationModalOpen: boolean;
    forceValidation: boolean;
    addressValidationPromptRequired: boolean;
    inputAddress: ValidationAddressModel;
    addressValidationResult: AddressValidationResult;
    hideSuggestedAddress: boolean;
    saveEvent: React.FormEvent<HTMLFormElement>;
}

type FormErrorsState = {
    firstName?: React.ReactNode;
    lastName?: React.ReactNode;
    attention?: React.ReactNode;
    companyName?: React.ReactNode;
    address1?: React.ReactNode;
    address2?: React.ReactNode;
    address3?: React.ReactNode;
    address4?: React.ReactNode;
    country?: React.ReactNode;
    city?: React.ReactNode;
    state?: React.ReactNode;
    postalCode?: React.ReactNode;
    phone?: React.ReactNode;
    email?: React.ReactNode;
    fax?: React.ReactNode;
};

export interface CustomerAddressFormStyles {
    form?: InjectableCss;
    formFields?: CustomerAddressFormFieldStyles;
    formButtonsWrapper?: InjectableCss;
    cancelButton?: ButtonPresentationProps;
    submitButton?: ButtonPresentationProps;
}

export const customerAddressFormStyles: CustomerAddressFormStyles = {
    formButtonsWrapper: {
        css: css`
            display: flex;
            justify-content: flex-end;
            margin-top: 30px;
        `,
    },
    submitButton: {
        css: css`
            color: white;
        `,
    },
    cancelButton: {
        variant: "secondary",
        css: css`
            margin-right: 10px;
        `,
    },
};

const styles = customerAddressFormStyles;
const StyledForm = getStyledWrapper("form");

const getRequiredErrorMessage = (fieldDisplay: AddressFieldDisplayModel) =>
    siteMessage("Field_Required", fieldDisplay.displayName);

class CustomerAddressForm<Address extends BillToModel | ShipToModel> extends React.Component<
    OwnProps<Address> & Props,
    State<Address>
> {
    private styles: CustomerAddressFormStyles;

    constructor(props: OwnProps<Address> & Props) {
        super(props);

        this.styles = mergeToNew(styles, props.extendedStyles);

        const { address } = this.props;
        const countryLookup = this.props.countries.find(c => c.id === address.country?.id);

        const inAddress = {} as ValidationAddressModel;
        inAddress.address1 = address.address1;
        inAddress.address2 = address.address2;
        inAddress.city = address.city;
        inAddress.postalCode = address.postalCode;
        inAddress.state = address.state?.abbreviation || "";
        inAddress.country = address.country?.abbreviation || "";
        this.state = {
            address,
            countryLookup,
            formErrors: {},
            formSubmitAttempted: false,
            isSubmitting: false,
            isValidationModalOpen: false,
            forceValidation: false,
            addressValidationPromptRequired: false,
            inputAddress: inAddress,
            addressValidationResult: {} as AddressValidationResult,
            saveEvent: {} as React.FormEvent<HTMLFormElement>,
            hideSuggestedAddress: false,
        };
    }

    componentDidUpdate(_: OwnProps<Address>, prevState: State<Address>) {
        if (prevState.address !== this.state.address && this.state.formSubmitAttempted) {
            this.validateForm();
        }
    }

    handleInputChange: TextFieldProps["onChange"] = event => {
        const { target } = event;
        const propertyName = target.name;
        this.setState({
            address: {
                ...this.state.address,
                [propertyName]: target.value,
            },
        });
    };

    formatPhoneNumber = (phoneNumberString: string) => {
        const formattedPhone = phoneNumberString.replace(/\D+/g, "").replace(/(\d{3})(\d{3})(\d{4})/, "($1) $2-$3");
        return formattedPhone;
    };

    handlePhoneNumberChange: TextFieldProps["onChange"] = event => {
        const { target } = event;
        const propertyName = target.name;
        const formattedPhoneNumber = this.formatPhoneNumber(target.value);
        this.setState({
            address: {
                ...this.state.address,
                [propertyName]: formattedPhoneNumber,
            },
        });
    };

    handleCountryChange = (_: React.ChangeEvent<HTMLSelectElement>, country: BaseAddressModel["country"]) => {
        const countryLookup = this.props.countries.find(c => c.id === country?.id);
        this.setState({
            address: {
                ...this.state.address,
                country,
                state: null,
            },
            countryLookup,
        });
    };

    handleStateChange = (_: React.ChangeEvent<HTMLSelectElement>, state: BaseAddressModel["state"]) =>
        this.setState({
            address: {
                ...this.state.address,
                state,
            },
        });

    validateForm = () => {
        const fieldDisplay = this.props.addressFieldDisplayCollection;
        const validation = this.props.address.validation!;
        const { countries } = this.props;
        const {
            firstName,
            lastName,
            attention,
            companyName,
            address1,
            address2,
            address3,
            address4,
            country,
            city,
            state,
            postalCode,
            phone,
            email,
            fax,
        } = this.state.address;

        const formErrors: FormErrorsState = {};
        if (fieldDisplay.firstName?.isVisible && validation.firstName?.isRequired && firstName.trim().length <= 0) {
            formErrors.firstName = getRequiredErrorMessage(fieldDisplay.firstName);
        }

        if (fieldDisplay.lastName?.isVisible && validation.lastName?.isRequired && lastName.trim().length <= 0) {
            formErrors.lastName = getRequiredErrorMessage(fieldDisplay.lastName);
        }

        if (fieldDisplay.attention?.isVisible && validation.attention?.isRequired && attention.trim().length <= 0) {
            formErrors.attention = getRequiredErrorMessage(fieldDisplay.attention);
        }

        if (
            fieldDisplay.companyName?.isVisible &&
            validation.companyName?.isRequired &&
            companyName.trim().length <= 0
        ) {
            formErrors.companyName = getRequiredErrorMessage(fieldDisplay.companyName);
        }

        if (fieldDisplay.address1?.isVisible && validation.address1?.isRequired && address1.trim().length <= 0) {
            formErrors.address1 = getRequiredErrorMessage(fieldDisplay.address1);
        }

        if (fieldDisplay.address2?.isVisible && validation.address2?.isRequired && address2.trim().length <= 0) {
            formErrors.address2 = getRequiredErrorMessage(fieldDisplay.address2);
        }

        if (fieldDisplay.address3?.isVisible && validation.address3?.isRequired && address3.trim().length <= 0) {
            formErrors.address3 = getRequiredErrorMessage(fieldDisplay.address3);
        }

        if (fieldDisplay.address4?.isVisible && validation.address4?.isRequired && address4.trim().length <= 0) {
            formErrors.address4 = getRequiredErrorMessage(fieldDisplay.address4);
        }

        if (countries.length > 0 && !country && fieldDisplay.country) {
            formErrors.country = getRequiredErrorMessage(fieldDisplay.country);
        }

        if (fieldDisplay.city?.isVisible && validation.city?.isRequired && city.trim().length <= 0) {
            formErrors.city = getRequiredErrorMessage(fieldDisplay.city);
        }

        const { countryLookup } = this.state;
        if (
            countries.length > 0 &&
            countryLookup?.states &&
            countryLookup.states.length > 0 &&
            !state &&
            fieldDisplay.state
        ) {
            formErrors.state = getRequiredErrorMessage(fieldDisplay.state);
        }

        if (fieldDisplay.postalCode?.isVisible && validation.postalCode?.isRequired && postalCode.trim().length <= 0) {
            formErrors.postalCode = getRequiredErrorMessage(fieldDisplay.postalCode);
        }

        if (fieldDisplay.phone?.isVisible && validation.phone?.isRequired && phone.trim().length <= 0) {
            formErrors.phone = getRequiredErrorMessage(fieldDisplay.phone);
        } else if (phone.trim().length > 0 && !validatePhone(phone)) {
            formErrors.phone = siteMessage("AddressInfo_Phone_Validation");
        }

        if (fieldDisplay.email?.isVisible && validation.email?.isRequired && email.trim().length <= 0) {
            formErrors.email = getRequiredErrorMessage(fieldDisplay.email);
        } else if (email.trim().length > 0 && !validateEmail(email)) {
            formErrors.email = siteMessage("AddressInfo_EmailAddress_Validation");
        }

        if (fieldDisplay.fax?.isVisible && validation.fax?.isRequired && fax.trim().length <= 0) {
            formErrors.fax = getRequiredErrorMessage(fieldDisplay.fax);
        } else if (fax.trim().length > 0 && !validatePhone(fax)) {
            formErrors.fax = siteMessage("AddressInfo_Phone_Validation");
        }

        this.setState({ formErrors });

        return formErrors;
    };

    submitHandler = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        event.stopPropagation();
        this.setState({ saveEvent: event });

        this.setState({ formSubmitAttempted: true, isSubmitting: true });

        const formErrors = this.validateForm();

        this.setState({ formErrors }, () => {
            if (Object.keys(this.state.formErrors).length === 0) {
                const inAddress = {} as ValidationAddressModel;
                inAddress.addressType = "generic";
                inAddress.address1 = this.state.address.address1;
                inAddress.address2 = this.state.address.address2 || "";
                inAddress.city = this.state.address.city;
                inAddress.postalCode = this.state.address.postalCode;
                inAddress.state = this.state.address.state?.abbreviation || "";
                inAddress.country = this.state.address.country?.abbreviation || "";

                this.props.validateInputAddress({
                    inputAddress: inAddress,
                    addressFieldDisplayCollection: this.props?.addressFieldDisplayCollection,
                    onComplete: response => {
                        const { ...apiResult } = response.apiResult as AddressValidationResultModel;
                        this.setState({ addressValidationResult: apiResult.validationResult! });
                        this.setState({ hideSuggestedAddress: false });
                        this.setState({ inputAddress: inAddress });

                        if (apiResult.validationResult.address !== undefined && apiResult.validationResult.dpv) {
                            if (
                                inAddress.address1.toUpperCase() !==
                                    (apiResult.validationResult.address.address1?.toUpperCase() || "") ||
                                inAddress.address2.toUpperCase() !==
                                    (apiResult.validationResult.address.address2?.toUpperCase() || "") ||
                                inAddress.city.toUpperCase() !== (apiResult.validationResult.address.city?.toUpperCase() || "") ||
                                inAddress.state.toUpperCase() !== (apiResult.validationResult.address.state?.toUpperCase() || "") ||
                                inAddress.postalCode !== (apiResult.validationResult.address.postalCode || "")
                            ) {
                                this.setState({ addressValidationPromptRequired: true });
                                this.setState({ isValidationModalOpen: true });
                                this.setState({
                                    isSubmitting: false,
                                });
                            } else {
                                this.postSubmit();
                            }
                        } else if (
                            !apiResult.validationResult.dpv &&
                            !apiResult.validationResult.invalidSuiteNumber &&
                            !apiResult.validationResult.suiteRequiredButMissing
                        ) {
                            this.setState({ addressValidationPromptRequired: true });
                            this.setState({ hideSuggestedAddress: true });
                            this.setState({ isValidationModalOpen: true });
                            this.setState({
                                isSubmitting: false,
                            });
                        } else {
                            formErrors.address1 = "Please enter a valid address.";
                            formErrors.address2 = inAddress.address2.length > 0 ? " " : "";
                            if (apiResult.validationResult.invalidSuiteNumber) {
                                formErrors.address1 = " ";
                                formErrors.address2 = "Please verify that address 2 is valid";
                            }
                            if (apiResult.validationResult.suiteRequiredButMissing) {
                                formErrors.address1 = " ";
                                formErrors.address2 = "Suite/Apt/Bldg required";
                            }
                            formErrors.city = " ";
                            formErrors.state = " ";
                            formErrors.postalCode = " ";
                            formErrors.country = " ";
                            formErrors.postalCode = " ";
                            this.setState({ formErrors });
                            this.setState({ isSubmitting: false });
                        }
                    },
                });
            } else {
                this.setState({
                    isSubmitting: false,
                });
            }
        });
    };

    validationModalCloseHandler = (e: any) => {
        e.preventDefault();
        this.setState({
            isValidationModalOpen: false,
            isSubmitting: false,
        });
    };

    acceptValidationResultHandler = () => {
        this.setState({
            isValidationModalOpen: false,
        });
        const country = this.props.countries?.find(
            c => c.abbreviation === this.state.addressValidationResult.address.country,
        );
        const selectedState = country?.states?.find(
            s => s.abbreviation === this.state.addressValidationResult.address.state,
        );
        const addressProps = cloneDeep(this.state.address.properties);
        addressProps["addressClassification"] = this.state.addressValidationResult.classification;
        this.setState(
            {
                address: {
                    ...this.state.address,
                    address1: this.state.addressValidationResult.address.address1 || "",
                    address2: this.state.addressValidationResult.address.address2 || "",
                    city: this.state.addressValidationResult.address.city,
                    state: selectedState,
                    country,
                    postalCode: this.state.addressValidationResult.address.postalCode,
                    properties: addressProps,
                },
            },
            () => {
                this.postSubmit();
            },
        );
    };

    postSubmit = () => {
        if (Object.keys(this.state.formErrors).length === 0) {
            const addressClassification = this.state.addressValidationResult.classification || "";
            const address = cloneDeep(this.props.address);
            address.properties["addressClassification"] = addressClassification;
            this.props.onSubmit(this.state.saveEvent, { ...address, ...this.state.address });
            // await this.props.updateCart({addressClassification});
        } else {
            this.setState({
                isSubmitting: false,
            });
        }
    };

    acceptEnteredAddressHandler = () => {
        this.setState({
            isValidationModalOpen: false,
        });

        if (this.state.addressValidationResult) {
            const addressProps = cloneDeep(this.state.address.properties);
            addressProps["addressClassification"] = this.state.addressValidationResult.classification;
            this.setState(
                {
                    address: {
                        ...this.state.address,
                        properties: addressProps,
                    },
                },
                () => {
                    this.postSubmit();
                },
            );
        }
    };

    render() {
        const {
            firstName,
            lastName,
            attention,
            companyName,
            address1,
            address2,
            address3,
            address4,
            country,
            city,
            state,
            postalCode,
            phone,
            email,
            fax,
        } = this.state.address;
        const { formErrors } = this.state;
        const { countries, extendedStyles } = this.props;
        const { validation } = this.props.address;
        const fieldDisplay = this.props.addressFieldDisplayCollection;

        if (!validation) {
            return null;
        }
        const isSaveDisabled =
            (validation.firstName!.isDisabled &&
                validation.lastName!.isDisabled &&
                validation.companyName!.isDisabled &&
                validation.attention!.isDisabled &&
                validation.address1!.isDisabled &&
                validation.address2!.isDisabled &&
                validation.address3!.isDisabled &&
                validation.address4!.isDisabled &&
                validation.country!.isDisabled &&
                validation.city!.isDisabled &&
                validation.postalCode!.isDisabled &&
                validation.phone!.isDisabled &&
                validation.fax!.isDisabled &&
                validation.email!.isDisabled) ||
            this.state.isSubmitting;

        return (
            <StyledForm {...styles.form} onSubmit={this.submitHandler} noValidate>
                <CustomerAddressFormFields
                    firstName={firstName}
                    lastName={lastName}
                    attention={attention}
                    companyName={companyName}
                    address1={address1}
                    address2={address2}
                    address3={address3}
                    address4={address4}
                    country={country}
                    city={city}
                    state={state}
                    postalCode={postalCode}
                    phone={phone}
                    email={email}
                    fax={fax}
                    onChangeFirstName={this.handleInputChange}
                    onChangeLastName={this.handleInputChange}
                    onChangeAttention={this.handleInputChange}
                    onChangeCompanyName={this.handleInputChange}
                    onChangeAddress1={this.handleInputChange}
                    onChangeAddress2={this.handleInputChange}
                    onChangeAddress3={this.handleInputChange}
                    onChangeAddress4={this.handleInputChange}
                    onChangeCountry={this.handleCountryChange}
                    onChangeState={this.handleStateChange}
                    onChangeCity={this.handleInputChange}
                    onChangePostalCode={this.handleInputChange}
                    onChangePhone={this.handlePhoneNumberChange}
                    onChangeEmail={this.handleInputChange}
                    onChangeFax={this.handleInputChange}
                    firstNameError={formErrors.firstName}
                    lastNameError={formErrors.lastName}
                    attentionError={formErrors.attention}
                    companyNameError={formErrors.companyName}
                    address1Error={formErrors.address1}
                    address2Error={formErrors.address2}
                    address3Error={formErrors.address3}
                    address4Error={formErrors.address4}
                    countryError={formErrors.country}
                    stateError={formErrors.state}
                    cityError={formErrors.city}
                    postalCodeError={formErrors.postalCode}
                    phoneError={formErrors.phone}
                    emailError={formErrors.email}
                    faxError={formErrors.fax}
                    countries={countries}
                    validation={validation}
                    fieldDisplay={fieldDisplay}
                    extendedStyles={extendedStyles?.formFields}
                />
                <StyledWrapper {...this.styles.formButtonsWrapper}>
                    {this.props.onCancel && (
                        <Button
                            {...this.styles.cancelButton}
                            type="button"
                            onClick={this.props.onCancel}
                            data-test-selector="customerAddressForm_cancel"
                        >
                            {translate("Cancel")}
                        </Button>
                    )}
                    <Button
                        {...this.styles.submitButton}
                        type="submit"
                        disabled={isSaveDisabled}
                        data-test-selector="customerAddressForm_save"
                    >
                        {this.props.saveButtonTextOverride ?? translate("Save")}
                    </Button>
                    <AddressValidationModal
                        isModalOpen={this.state.isValidationModalOpen}
                        forceValidation={this.state.forceValidation}
                        addressValidationPromptRequired={this.state.addressValidationPromptRequired}
                        onModalClose={this.validationModalCloseHandler}
                        onAcceptModal={this.acceptValidationResultHandler}
                        onContinueModal={this.acceptEnteredAddressHandler}
                        addressFieldDisplayCollection={this.props.addressFieldDisplayCollection}
                        inputAddress={this.state.inputAddress}
                        address={this.state.addressValidationResult.address}
                        hideSuggestedAddress={this.state.hideSuggestedAddress}
                    />
                </StyledWrapper>
            </StyledForm>
        );
    }
}
export default connect(mapStateToProps, mapDispatchToProps)(CustomerAddressForm);
